import React, { useState } from "react";
import Slider from "react-slick";
import AboutModal from "./AboutModal";
// eslint-disable-next-line no-unused-vars
import resumeFile from "../documents/resume.pdf";

// Dynamic PDF generator import
const generateCVPDF = async () => {
  try {
    const { generateCVPDF: pdfGenerator } = await import("../services/pdfGenerator");
    return pdfGenerator();
  } catch (error) {
    console.error('Error loading PDF generator:', error);
    alert('PDF generation is not available in development mode');
  }
};

const WhoIWorkFor = ({ classicHeader, darkTheme }) => {
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadCV = async () => {
    try {
      setIsGenerating(true);
      await generateCVPDF();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const educationDetails = [
    {
      yearRange: "2023",
      title: "React.js",
      place: "CoderHouse",
      link: "https://www.coderhouse.com/ar/cursos/reactjs"
    },
    {
      yearRange: "2023",
      title: "JavaScript",
      place: "CoderHouse",
      link: "https://www.coderhouse.com/ar/cursos/javascript"
    },
    {
      yearRange: "2015 - 2018",
      title: "Systems Analyst",
      place: "Escuela Da Vinci",
      link: "https://davinci.edu.ar/carreras/analista-de-sistemas"
    },
  ];

  const experienceDetails = [
    {
      yearRange: "2026 - Present",
      title: "Web Development Lead",
      place: "Roar Media",
      flag: "https://flagcdn.com/us.svg",
      link: "https://roarmedia.com"
    },
    {
      yearRange: "2024 - 2026",
      title: "Senior Web Developer",
      place: "Roar Media",
      flag: "https://flagcdn.com/us.svg",
      link: "https://roarmedia.com"
    },
    {
      yearRange: "2022 - 2024",
      title: "Web Developer",
      place: "Roar Media",
      flag: "https://flagcdn.com/us.svg",
      link: "https://roarmedia.com"
    },
    {
      yearRange: "2022 - Present",
      title: "Web Department Manager",
      place: "Motus Creativo",
      flag: "https://flagcdn.com/ar.svg",
      link: "https://motuscreativo.com"
    },
    {
      yearRange: "2021 - 2022",
      title: "WordPress Developer",
      place: "Dimensiona",
      flag: "https://flagcdn.com/es.svg",
      link: "https://dimensiona.com"
    },
  ];
  const sectors = [
    {
      icon: "fas fa-bullseye",
      color: "#6C63FF",
      title: "Marketing Agencies",
      subtitle: "Digital Marketing & Brand Development",
      description: "Partnering with marketing agencies to deliver high-performance WordPress solutions, custom web applications, and conversion-optimized websites. Specializing in scalable solutions that support diverse client portfolios and complex campaign requirements."
    },
    {
      icon: "fas fa-shopping-cart",
      color: "#FF6B6B",
      title: "E-commerce & Retail",
      subtitle: "Online Stores & Digital Commerce",
      description: "Creating powerful e-commerce platforms using WooCommerce and Shopify, custom integrations, and performance optimizations. From product catalogs to complex payment gateways, delivering solutions that drive sales and enhance customer experience."
    },
    {
      icon: "fas fa-heartbeat",
      color: "#4ECDC4",
      title: "Healthcare Industry",
      subtitle: "Medical & Health Services",
      description: "Specialized experience working with healthcare organizations, developing secure medical websites, e-learning platforms for medical training, authorization apps, and HIPAA-compliant solutions. Ensuring reliability and security for critical healthcare applications."
    }
  ];

  var settings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
    ],
  };

  return (
    <section
      id="sectors"
      className={"section " + (darkTheme ? "bg-dark-1" : "")}
    >
      <div className={"container " + (classicHeader ? "" : "px-lg-5")}>
        {/* Heading */}
        <div data-reveal className="position-relative d-flex text-center mb-5">
          <h2
            className={
              "text-24  text-uppercase fw-600 w-100 mb-0 " +
              (darkTheme ? "text-muted opacity-1" : "text-light opacity-4")
            }
          >
            SECTORS
          </h2>
          <p
            className={
              "text-9 text-dark fw-600 position-absolute w-100 align-self-center lh-base mb-0 " +
              (darkTheme ? "text-white" : "text-dark")
            }
          >
            Who I Work For
            <span className="heading-separator-line border-bottom border-3 border-primary d-block mx-auto" />
          </p>
        </div>
        {/* Heading end*/}

        {/* Sectors Cards */}
        {/* Desktop Grid */}
        <div className="row g-4 d-none d-lg-flex">
          {sectors.map((sector, index) => (
            <div key={index} className="col-lg-4">
              <div data-reveal data-reveal-delay={index * 0.1} className={`card h-100 ${darkTheme ? "bg-dark text-light" : ""}`}>
                <div className="card-body text-center p-4">
                  <div className="sector-icon mb-4">
                    <i 
                      className={sector.icon} 
                      style={{ 
                        fontSize: "3rem", 
                        color: sector.color 
                      }}
                    />
                  </div>
                  <h5 className={`card-title fw-600 mb-2 ${darkTheme ? "text-white" : ""}`}>
                    {sector.title}
                  </h5>
                  <h6 className={`card-subtitle mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                    {sector.subtitle}
                  </h6>
                  <p 
                    className={`card-text ${darkTheme ? "text-white-50" : ""}`}
                    style={{ fontSize: '14px' }}
                  >
                    {sector.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div data-reveal className="d-lg-none">
          <Slider {...settings}>
            {sectors.length > 0 &&
              sectors.map((sector, index) => (
                <div className="item" key={index}>
                  <div className={`card h-100 mx-2 ${darkTheme ? "bg-dark text-light" : ""}`}>
                    <div className="card-body text-center p-4">
                      <div className="sector-icon mb-4">
                        <i 
                          className={sector.icon} 
                          style={{ 
                            fontSize: "3rem", 
                            color: sector.color 
                          }}
                        />
                      </div>
                      <h5 className={`card-title fw-600 mb-2 ${darkTheme ? "text-white" : ""}`}>
                        {sector.title}
                      </h5>
                      <h6 className={`card-subtitle mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                        {sector.subtitle}
                      </h6>
                      <p 
                        className={`card-text ${darkTheme ? "text-white-50" : ""}`}
                        style={{ fontSize: '14px' }}
                      >
                        {sector.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </Slider>
        </div>

        {/* Resume Button */}
        <div className="text-center mt-5">
          <a
            className="btn btn-primary rounded-pill"
            href="/cv"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Resume
          </a>
        </div>

        {/* Resume Modal */}
        <AboutModal 
          show={showResumeModal} 
          onClose={() => setShowResumeModal(false)} 
          content={
            <div>
              <div className="row gx-5">
                <div className="col-md-6">
                  <h2 className={"text-6 fw-600 mb-4 " + (darkTheme ? "text-white" : "")}>My Education</h2>
                  {educationDetails.length > 0 && educationDetails.map((value, index) => (
                    <div key={index} className={"bg-white rounded p-4 mb-4 " + (darkTheme ? "bg-dark" : "bg-white border")}>
                      <p className="badge bg-primary text-2 fw-400">{value.yearRange}</p>
                      <h3 className={"text-5 " + (darkTheme ? "text-white" : "")}>{value.title}</h3>
                      <p className={darkTheme ? "text-primary" : "text-danger"} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        {value.place}
                        {value.link && (
                          <a href={value.link} target="_blank" rel="noopener noreferrer" style={{marginLeft: '4px'}}>
                            <i className="fas fa-external-link-alt" />
                          </a>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="col-md-6">
                  <h2 className={"text-6 fw-600 mb-4 " + (darkTheme ? "text-white" : "")}>My Experience</h2>
                  {experienceDetails.length > 0 && experienceDetails.map((value, index) => (
                    <div key={index} className={"bg-white rounded p-4 mb-4 " + (darkTheme ? "bg-dark" : "bg-white border")}>
                      <p className="badge bg-primary text-2 fw-400">{value.yearRange}</p>
                      <h3 className={"text-5 " + (darkTheme ? "text-white" : "")}>{value.title}</h3>
                      <p className={darkTheme ? "text-primary" : "text-danger"} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        {value.place}
                        {value.flag && (
                          <img src={value.flag} alt="flag" style={{width: '20px', height: '14px', objectFit: 'cover'}} />
                        )}
                        {value.link && (
                          <a href={value.link} target="_blank" rel="noopener noreferrer" style={{marginLeft: '4px'}}>
                            <i className="fas fa-external-link-alt" />
                          </a>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center mt-4">
                <button
                  className="btn btn-outline-secondary rounded-pill shadow-none"
                  onClick={handleDownloadCV}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Download CV'}
                  <span className="ms-1">
                    <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-download'}`} />
                  </span>
                </button>
              </div>
            </div>
          } 
        />
      </div>
    </section>
  );
};

export default WhoIWorkFor;